import {FOLDER_TAGS} from 'app/folders/api';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
  tagsRelatedTo,
} from 'app/store/sweet-api/utils';
import {DOCUMENTS_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {DEFAULT_PAGE_SIZE} from '../utils/constants';

import type {
  AddDocToFolderProps,
  DashboardFetchParamsType,
  DashboardLocationParamsType,
  DocumentFetchParamsType,
  RemoveDocProps,
} from 'app/documents/types';
import type {
  BasicTeamSerializedType,
  DocumentSerializedType,
  DocumentTeamMembershipSerializedType,
  ListResponseType,
} from 'sweet-types';

export const DOCUMENT_TAG = 'document';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [DOCUMENT_TAG, FOLDER_TAGS.TAGS]})
  .injectEndpoints({
    endpoints: (build) => ({
      documents: build.query<
        ListResponseType<DocumentSerializedType>,
        DocumentFetchParamsType
      >({
        query: (params) => ({
          url: DOCUMENTS_API_URL,
          params,
        }),
        providesTags: providesTagsByURL(DOCUMENT_TAG),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load documents.'),
          })(args, api);
        },
      }),
      removeFromFolder: build.mutation<unknown, RemoveDocProps>({
        query: ({id}) => ({
          url: `${DOCUMENTS_API_URL}${id}/`,
          method: 'DELETE',
        }),
        invalidatesTags: (
          result,
          error,
          {document_content_type, documentURL}
        ) => [
          {type: DOCUMENT_TAG, id: 'list'},
          {type: FOLDER_TAGS.TAGS, id: 'list'},
          ...tagsRelatedTo({
            contentType: document_content_type,
            url: documentURL,
          }),
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to remove the document.'),
            onSuccess: ({name}) =>
              gt('Document %s has been removed.', name || ''),
          })(args, api);
        },
      }),
      addToFolder: build.mutation<unknown, AddDocToFolderProps>({
        query: (body) => ({url: DOCUMENTS_API_URL, method: 'POST', body}),
        invalidatesTags: (
          result,
          error,
          {document_content_type, documentURL}
        ) => [
          {type: DOCUMENT_TAG, id: 'list'},
          {type: FOLDER_TAGS.TAGS, id: 'list'},
          ...tagsRelatedTo({
            contentType: document_content_type,
            url: documentURL,
          }),
        ],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to add to folder.'),
            onSuccess: () => gt('Added to the folder'),
          })(args, api);
        },
      }),

      getDocumentMemberships: build.query<
        Array<DocumentTeamMembershipSerializedType & {name: string}>,
        {url: string}
      >({
        query: ({url}) => ({
          url: DOCUMENTS_API_URL,
          params: {document_url: url, folder: ''},
        }),
        providesTags: () => [{type: DOCUMENT_TAG, id: 'list'}],
        transformResponse: (
          response: ListResponseType<
            DocumentSerializedType,
            {basic_team: Record<number, BasicTeamSerializedType>}
          >
        ) => {
          // using knowledge of documents api to get only one doc but in list format.
          // we do this because no other way to turn procedure url into a document connection.
          const doc = response.results[0];

          if (doc == null) {
            return [];
          }

          const result = doc.document.team_memberships
            .filter(
              (membership) =>
                membership.team != null && membership.team.match(/\d+/g) != null
            )
            .map((membership: DocumentTeamMembershipSerializedType) => {
              const matches = membership.team.match(/\d+/g)!;
              const teamId = matches[matches.length - 1];
              return {
                ...membership,
                name: response.related?.basic_team[teamId].name,
                team_html_url: response.related?.basic_team[teamId].html_url,
              };
            });
          return result;
        },
      }),
      getDocument: build.query<
        DocumentSerializedType,
        {id?: number; url?: string}
      >({
        query: ({id, url}) => ({
          url: url || `${DOCUMENTS_API_URL}/${id}/`,
        }),
        providesTags: (response) =>
          response != null
            ? [
                {
                  type: DOCUMENT_TAG,
                  id: response.url,
                },
              ]
            : [],
      }),
      deleteMembership: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          url: url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => gt('Removed document'),
          onError: () => gt('Unable to remove document at the moment'),
        }),
        invalidatesTags: () => [{type: DOCUMENT_TAG, id: 'list'}],
      }),
    }),
  });

export const {
  useDocumentsQuery: useDocuments,
  useRemoveFromFolderMutation: useRemoveFromFolder,
  useAddToFolderMutation: useAddToFolder,
  useGetDocumentQuery: useDocument,
  useGetDocumentMembershipsQuery: useDocumentMemberships,
  useDeleteMembershipMutation: useDeleteDocumentMembership,
} = extendedAPI;

export const convertLocationParamsToFetchParams = (
  query: DashboardLocationParamsType
): DashboardFetchParamsType => {
  const filters = query.filter != null ? query.filter.split(',') : [];

  return {
    team_id:
      query.team_id != null && query.team_id !== '-1'
        ? query.team_id
        : undefined,
    in_no_teams: query.team_id === '-1' ? '1' : undefined,
    exclude_team_id: query.exclude_team_id,
    page: query.page != null ? query.page : '1',
    page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
    search: query.search,
    ordering: query.ordering,
    document_url: query.document_url,
    private:
      filters.indexOf('private') > -1
        ? 'true'
        : filters.indexOf('public') > -1
        ? 'false'
        : undefined,
    tag: query.tag != null ? query.tag : undefined,
    visible_to_user: query.visible_to_user,
    filter: query.filter,
    folder: query.folder || '', // forces to fetch from top level
  };
};
